// Passkey (WebAuthn) calls against Nexus /auth/mfa/* — NEXUS-757. No new crypto: the server issues the options and
// verifies the result; the browser's navigator.credentials does the rest. `hybrid` transport is on by default, so a
// computer without biometrics offers "use a phone or tablet" (QR code, approve with Face ID).
//
// TODO (later step): Sign in with Apple / Google on the web console. Not built here.
import { ApiError, setTokens } from './api';
import { authCall } from './authCall';

export interface PasskeyInfo { id: string; name: string; createdAt: string; lastUsedAt: string | null; backedUp: boolean }
export interface MfaStatus { enrolled: boolean; satisfied: boolean; passkeys: PasskeyInfo[]; recoveryCodesRemaining: number; webauthnConfigured: boolean }

export function b64urlToBuf(s: string): ArrayBuffer {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const bin = atob((s + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
}
export function bufToB64url(b: ArrayBuffer | null | undefined): string {
  if (!b) return '';
  let s = '';
  for (const x of new Uint8Array(b)) s += String.fromCharCode(x);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

interface DescriptorJSON { id: string; type: 'public-key'; transports?: AuthenticatorTransport[] }
const toDescriptors = (l?: DescriptorJSON[]): PublicKeyCredentialDescriptor[] | undefined =>
  l?.map((d) => ({ type: d.type, id: b64urlToBuf(d.id), ...(d.transports ? { transports: d.transports } : {}) }));

/** Server options (JSON) -> the browser's publicKey request options. */
export function requestOptionsFromJson(o: Record<string, unknown>): PublicKeyCredentialRequestOptions {
  return { ...(o as object), challenge: b64urlToBuf(o.challenge as string), allowCredentials: toDescriptors(o.allowCredentials as DescriptorJSON[] | undefined) } as PublicKeyCredentialRequestOptions;
}
export function creationOptionsFromJson(o: Record<string, unknown>): PublicKeyCredentialCreationOptions {
  const user = o.user as { id: string; name: string; displayName: string };
  return {
    ...(o as object), challenge: b64urlToBuf(o.challenge as string),
    user: { ...user, id: b64urlToBuf(user.id) },
    excludeCredentials: toDescriptors(o.excludeCredentials as DescriptorJSON[] | undefined),
  } as PublicKeyCredentialCreationOptions;
}

export function assertionToJson(c: PublicKeyCredential): Record<string, unknown> {
  const r = c.response as AuthenticatorAssertionResponse;
  return { id: c.id, rawId: bufToB64url(c.rawId), type: c.type, clientExtensionResults: c.getClientExtensionResults(),
    authenticatorAttachment: c.authenticatorAttachment ?? undefined,
    response: { clientDataJSON: bufToB64url(r.clientDataJSON), authenticatorData: bufToB64url(r.authenticatorData), signature: bufToB64url(r.signature), userHandle: r.userHandle ? bufToB64url(r.userHandle) : undefined } };
}
export function attestationToJson(c: PublicKeyCredential): Record<string, unknown> {
  const r = c.response as AuthenticatorAttestationResponse;
  return { id: c.id, rawId: bufToB64url(c.rawId), type: c.type, clientExtensionResults: c.getClientExtensionResults(),
    authenticatorAttachment: c.authenticatorAttachment ?? undefined,
    response: { clientDataJSON: bufToB64url(r.clientDataJSON), attestationObject: bufToB64url(r.attestationObject),
      transports: typeof r.getTransports === 'function' ? r.getTransports() : undefined } };
}

// L3: through authCall, so an expired access token is refreshed once and the call retried.
async function mfa<T>(method: 'GET' | 'POST' | 'DELETE', path: string, body?: unknown): Promise<T> {
  const r = await authCall(method, `mfa/${path}`, body);
  if (r.status < 200 || r.status >= 300) {
    throw new ApiError(r.status, typeof r.body.message === 'string' ? r.body.message : `HTTP ${r.status}`, typeof r.body.code === 'string' ? r.body.code : undefined, r.body);
  }
  return r.body as T;
}

export const passkeysSupported = (): boolean => typeof window !== 'undefined' && !!window.PublicKeyCredential && !!navigator.credentials;
export const mfaStatus = (): Promise<MfaStatus> => mfa('GET', 'status');

function adoptStepUp(r: { accessToken?: string }): void { if (r.accessToken) setTokens(r.accessToken); }

/** Sign-in step: ask for a passkey assertion; on success the stepped-up access token replaces the current one. */
export async function assertPasskey(): Promise<void> {
  const { options } = await mfa<{ options: Record<string, unknown> }>('POST', 'assert/options');
  let cred: Credential | null;
  try { cred = await navigator.credentials.get({ publicKey: requestOptionsFromJson(options) }); }
  catch (e) { throw new ApiError(400, e instanceof DOMException && e.name === 'NotAllowedError' ? 'The passkey prompt was cancelled or timed out.' : 'Could not use a passkey here.', 'PASSKEY_CANCELLED'); }
  if (!cred) throw new ApiError(400, 'No passkey was provided.', 'PASSKEY_CANCELLED');
  adoptStepUp(await mfa<{ accessToken?: string }>('POST', 'assert/verify', { response: assertionToJson(cred as PublicKeyCredential) }));
}

/**
 * Add a passkey. The first one on an account needs the password (re-entered); later ones need a passkey step-up
 * (which the sign-in already did). Returns the one-time recovery codes when this was the first passkey.
 */
export async function enrollPasskey(name: string, password?: string): Promise<{ recoveryCodes?: string[] }> {
  const { options } = await mfa<{ options: Record<string, unknown> }>('POST', 'passkeys/registration/options', { name, ...(password ? { password } : {}) });
  let cred: Credential | null;
  try { cred = await navigator.credentials.create({ publicKey: creationOptionsFromJson(options) }); }
  catch (e) { throw new ApiError(400, e instanceof DOMException && e.name === 'NotAllowedError' ? 'Adding the passkey was cancelled or timed out.' : 'Could not create a passkey here.', 'PASSKEY_CANCELLED'); }
  if (!cred) throw new ApiError(400, 'No passkey was created.', 'PASSKEY_CANCELLED');
  const r = await mfa<{ accessToken?: string; recoveryCodes?: string[] }>('POST', 'passkeys/registration/verify', { response: attestationToJson(cred as PublicKeyCredential) });
  adoptStepUp(r);
  return { recoveryCodes: r.recoveryCodes };
}

export async function removePasskey(id: string): Promise<void> { await mfa('DELETE', `passkeys/${encodeURIComponent(id)}`); }
